import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useSalesInvoicingModuleStatus } from '../hooks/useSalesInvoicingModuleStatus';
import { reportsService, jobsService } from '../services';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import PCDrilldownDialog from '../components/reports/PCDrilldownDialog';
import { 
  Calendar, 
  Users, 
  Briefcase, 
  Home as HomeIcon, 
  User,
  Building2,
  Settings,
  BarChart3,
  CalendarDays,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  ExternalLink,
  X,
  Grid3X3,
  Mail,
  MoreHorizontal,
  FileText,
  Trophy,
  Warehouse,
  Smartphone,
  WifiOff,
  TrendingUp,
  ChevronRight,
  Receipt
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subDays } from 'date-fns';

export default function HomePage() {
  const { user, canManageSites, canManageAuditors, canManageJobs, isAdmin, isAuditor, isScheduler, isSeniorScheduler } = useAuth();
  const { region, formatCurrency } = useSettings();
  const navigate = useNavigate();
  const { enabled: salesInvoicingEnabled, loading: salesInvoicingStatusLoading } = useSalesInvoicingModuleStatus();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [auditorStats, setAuditorStats] = useState(null);  // Auditor-specific stats
  const [now, setNow] = useState(new Date());

  // Live-updating clock — lets users sanity-check the device/browser's
  // current time against their watch (e.g. when a scheduled notification
  // appears to arrive at the "wrong" hour).
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Drill-down state
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState('');
  const [drilldownDescription, setDrilldownDescription] = useState('');
  const [drilldownJobs, setDrilldownJobs] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  
  // Intelligence Flag PC drill-down state
  const [flagDdOpen, setFlagDdOpen]       = useState(false);
  const [flagDdTitle, setFlagDdTitle]     = useState('');
  const [flagDdDesc, setFlagDdDesc]       = useState('');
  const [flagDdType, setFlagDdType]       = useState('');
  const [flagDdRecords, setFlagDdRecords] = useState([]);
  const [flagDdLoading, setFlagDdLoading] = useState(false);
  const [flagDdRevenue, setFlagDdRevenue] = useState(null);
  const [flagDdBilling, setFlagDdBilling] = useState(null);

  // Auditor drilldown state
  const [auditorDrilldownOpen, setAuditorDrilldownOpen] = useState(false);
  const [auditorUsers, setAuditorUsers] = useState([]);
  const [auditorDrilldownLoading, setAuditorDrilldownLoading] = useState(false);

  // Business Pulse state (Admin/Scheduler only)
  const [businessPulse, setBusinessPulse] = useState(null);
  const [loadingPulse, setLoadingPulse] = useState(false);
  const [pulseError, setPulseError] = useState(null);
  
  // Determine if this is an auditor-only user
  const isAuditorOnly = isAuditor && !isAdmin && !isScheduler;

  // Track online/offline
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const today = new Date();
  const monthStart = startOfMonth(today);
  const monthEnd = endOfMonth(today);

  // Load dashboard stats - filtered by region
  // For auditor-only users, load their personal stats
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoadingStats(true);
        
        // Load team/company stats (for all users)
        const data = await reportsService.getDashboardStats({ region });
        setStats(data);
        
        // For auditor-only users, also load their personal stats
        if (isAuditorOnly) {
          try {
            // Get jobs assigned to this auditor for this month
            const auditorJobs = await jobsService.list({
              start_date: format(monthStart, 'yyyy-MM-dd'),
              end_date: format(monthEnd, 'yyyy-MM-dd'),
              region,
              limit: 5000
              // Note: Without for_reports=true, backend filters by auditor automatically
            });
            
            // Filter out leave events
            const clientJobs = auditorJobs.filter(j => !j.event_type?.startsWith('LEAVE'));
            
            // Calculate auditor-specific KPIs
            const myScheduled = clientJobs.filter(j => j.status === 'scheduled').length;
            const myCompleted = clientJobs.filter(j => j.status === 'completed').length;
            // A job is overdue only AFTER 1 full day past its scheduled date
            const overdueCutoff = subDays(today, 1);
            const myOverdue = clientJobs.filter(j => 
              j.status === 'scheduled' && 
              new Date(j.scheduled_date) < overdueCutoff
            ).length;
            const myTotal = clientJobs.length;
            
            setAuditorStats({
              scheduled_this_month: myScheduled,
              completed_this_month: myCompleted,
              overdue_count: myOverdue,
              total_jobs: myTotal,
              completion_rate: myTotal > 0 ? Math.round((myCompleted / myTotal) * 100) : 0
            });
          } catch (err) {
            console.error('Failed to load auditor stats', err);
            // Show zeroed stats on error rather than hiding the section entirely
            setAuditorStats({ scheduled_this_month: 0, completed_this_month: 0, overdue_count: 0, total_jobs: 0, completion_rate: 0 });
          }
        }
      } catch (err) {
        console.error('Failed to load stats', err);
      } finally {
        setLoadingStats(false);
      }
    };
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, isAuditorOnly]);  // Only re-run when region or auditor status changes

  // Load Business Pulse KPIs (Admin/Scheduler only)
  useEffect(() => {
    if (!isOnline || isAuditorOnly || (!isAdmin && !canManageSites)) return;
    const loadPulse = async () => {
      try {
        setLoadingPulse(true);
        setPulseError(null);
        const data = await reportsService.getBusinessPulse();
        setBusinessPulse(data);
      } catch (err) {
        console.error('Failed to load Business Pulse:', err);
        setPulseError('Could not load business metrics. Please refresh to try again.');
      } finally {
        setLoadingPulse(false);
      }
    };
    loadPulse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, canManageSites, isAuditorOnly, isOnline]);

  // Drill-down handler — uses dedicated backend endpoint with identical filters to KPI
  const handleKPIDrilldown = async (type, title, description) => {
    setDrilldownTitle(title);
    setDrilldownDescription(description);
    setDrilldownOpen(true);
    setDrilldownLoading(true);
    
    try {
      const jobs = await reportsService.getDashboardDrilldown({ type, region });
      setDrilldownJobs(jobs);
    } catch (err) {
      console.error('Failed to load drill-down data:', err);
      setDrilldownJobs([]);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Auditor drilldown handler - shows users with auditor role
  const handleAuditorDrilldown = async () => {
    setAuditorDrilldownOpen(true);
    setAuditorDrilldownLoading(true);
    
    try {
      const users = await reportsService.getAuditorUsers();
      setAuditorUsers(users);
    } catch (err) {
      console.error('Failed to load auditor users:', err);
      setAuditorUsers([]);
    } finally {
      setAuditorDrilldownLoading(false);
    }
  };

  // Intelligence Flag drill-down: opens PC-style drill-down for stale backlog / sites flags
  const handleFlagDrilldown = async (flag) => {
    if (!flag.drilldown_type) {
      // No drill-down defined — fall back to navigation
      navigate(flag.drill_to);
      return;
    }
    setFlagDdTitle(flag.title);
    setFlagDdDesc(flag.body);
    setFlagDdType(flag.drilldown_type);
    setFlagDdRecords([]);
    setFlagDdRevenue(null);
    setFlagDdBilling(null);
    setFlagDdOpen(true);
    setFlagDdLoading(true);
    try {
      const result = await reportsService.getPerformanceDrilldown({ type: flag.drilldown_type, period: 'ytd' });
      setFlagDdRecords(result.records || []);
      if (result.total_revenue != null) setFlagDdRevenue(result.total_revenue);
      if (result.total_billing != null) setFlagDdBilling(result.total_billing);
    } catch (err) {
      console.error('[Flag] Drill-down fetch failed:', err);
      setFlagDdRecords([]);
    } finally {
      setFlagDdLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: { variant: 'default', className: 'bg-blue-100 text-blue-700' },
      completed: { variant: 'default', className: 'bg-green-100 text-green-700' },
      cancelled: { variant: 'default', className: 'bg-gray-100 text-gray-700' },
      in_progress: { variant: 'default', className: 'bg-yellow-100 text-yellow-700' }
    };
    const config = variants[status] || variants.scheduled;
    return <Badge className={config.className}>{status}</Badge>;
  };

  // Navigation tiles based on role
  // For auditor-only users, show their personal stats instead of team stats
  const getScheduleStat = () => {
    if (loadingStats) return null;
    if (isAuditorOnly && auditorStats) {
      return `${auditorStats.scheduled_this_month || 0} your jobs`;
    }
    return `${stats?.scheduled_this_month || 0} this month`;
  };

  const navigationTiles = [
    {
      title: 'Schedule',
      description: isAuditorOnly ? 'View your assigned work' : 'Manage audit schedule',
      icon: Calendar,
      path: '/schedule',
      color: 'bg-blue-500',
      stat: getScheduleStat(),
      visible: true
    },
    {
      title: 'Calendar',
      description: 'Visual calendar view',
      icon: CalendarDays,
      path: '/calendar',
      color: 'bg-indigo-500',
      stat: null,
      visible: true
    },
    {
      title: 'Reports',
      description: user?.role === 'auditor' ? 'View your job reports' : 'KPI analysis & reports',
      icon: BarChart3,
      path: '/reports',
      color: 'bg-teal-500',
      // For auditors, use auditorStats; for admins/schedulers, use general stats
      stat: loadingStats ? null : (
        isAuditorOnly 
          ? (auditorStats?.overdue_count > 0 ? `${auditorStats.overdue_count} overdue` : 'All current')
          : (stats?.overdue_count > 0 ? `${stats.overdue_count} overdue` : 'All current')
      ),
      visible: true  // All internal users can access
    },
    {
      title: 'Audit Reports',
      description: 'Search and manage report files',
      icon: FileText,
      path: '/reports-repository',
      color: 'bg-violet-500',
      stat: null,
      visible: isAdmin || isScheduler || isAuditor  // Visible to all internal users
    },
    {
      title: 'Customers',
      description: 'Customers and sites',
      icon: Building2,
      path: '/customers',
      color: 'bg-emerald-500',
      stat: loadingStats ? null : `${stats?.total_clients || 0} customers`,
      visible: isAdmin || isAuditor  // Phase 1A: Auditors can view sites and edit limited fields
    },
    {
      title: 'Services',
      description: 'Service types',
      icon: Briefcase,
      path: '/services',
      color: 'bg-amber-500',
      stat: null,
      visible: isAdmin
    },
    {
      title: 'Incentives',
      description: 'Incentive runs & programmes',
      icon: Trophy,
      path: '/incentives',
      color: 'bg-orange-500',
      stat: null,
      visible: isAdmin || isSeniorScheduler
    },
    {
      title: 'Settings',
      description: 'Organisation config',
      icon: Settings,
      path: '/settings',
      color: 'bg-slate-500',
      stat: null,
      visible: isAdmin
    },
    {
      title: 'Stocktaking',
      description: 'Deliveries, credits & stock ops',
      icon: Warehouse,
      path: '/stocktaking/dashboard',
      color: 'bg-teal-600',
      stat: null,
      visible: isAdmin || isScheduler || isSeniorScheduler || isAuditor  // iter399: auditors get full stocktake access
    },
    {
      title: 'Apps',
      description: 'AbbeyZone & AbbeyZone Count',
      icon: Grid3X3,
      path: '/apps',
      color: 'bg-cyan-600',
      stat: null,
      visible: isAdmin || isScheduler || isSeniorScheduler || isAuditor  // iter399: auditors can launch the count tablet too
    },
    {
      title: 'Sales Invoicing',
      description: 'QBO reference lists & mapping workbench',
      icon: Receipt,
      path: '/sales-invoicing',
      color: 'bg-rose-600',
      stat: null,
      visible: (isAdmin || isSeniorScheduler) && !salesInvoicingStatusLoading && salesInvoicingEnabled  // 2026-11: hidden while Sales Invoicing is paused (and while status is still loading)
    }
  ];

  // Offline-only tiles — shown when offline instead of the full nav grid
  const offlineTiles = [
    {
      title: 'AbbeyZone Count',
      description: 'Tablet-optimised counting app',
      icon: Smartphone,
      path: '/count',
      color: 'bg-cyan-600',
      stat: null,
      visible: true,
    },
    {
      title: 'Offline Sessions',
      description: 'Download and work offline',
      icon: WifiOff,
      path: '/stocktaking/offline',
      color: 'bg-amber-500',
      stat: null,
      visible: true,
    },
  ];

  const visibleTiles = isOnline
    ? navigationTiles.filter(tile => tile.visible)
    : offlineTiles;

  return (
    <div data-testid="home-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="bg-slate-100 rounded-full p-2">
          <User className="h-6 w-6 text-slate-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            Welcome, {user?.first_name || user?.email?.split('@')[0] || 'User'}
          </h1>
          <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="home-date-time">
            {format(today, 'EEEE, MMMM d, yyyy')}
            <span className="text-slate-400">·</span>
            <span data-testid="home-system-time">
              {format(now, 'HH:mm:ss')} ({Intl.DateTimeFormat().resolvedOptions().timeZone})
            </span>
          </p>
        </div>
      </div>

      {isOnline && (isAdmin || canManageSites) && !isAuditorOnly && (
        <div data-testid="business-pulse-section">
          <h2 className="text-base font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {businessPulse?.presentation_level === 'operations'
              ? `Operations Pulse — ${format(today, 'MMMM yyyy')}`
              : `Business Pulse — ${format(today, 'MMMM yyyy')}`}
          </h2>

          {loadingPulse && (
            <div className="flex items-center gap-2 py-5 text-slate-400" data-testid="bp-loading">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading metrics…</span>
            </div>
          )}

          {pulseError && !loadingPulse && (
            <Card className="bg-red-50 border-red-200" data-testid="bp-error">
              <CardContent className="pt-3 pb-3">
                <p className="text-sm text-red-600">{pulseError}</p>
              </CardContent>
            </Card>
          )}

          {!loadingPulse && businessPulse && businessPulse.presentation_level === 'management' && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">

              {/* 1 — Forecast Revenue (Management only) */}
              <Card
                className="bg-emerald-50 border-emerald-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                onClick={() => handleFlagDrilldown({
                  drilldown_type: 'forecast_revenue',
                  title: `Forecast Revenue — ${format(today, 'MMMM yyyy')}`,
                  body: 'Includes all Scheduled and Completed jobs whose dates overlap with this month. Completed jobs contribute realised revenue; Scheduled jobs contribute expected revenue.',
                  drill_to: '/reports',
                })}
                data-testid="bp-forecast-revenue"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-7 w-7 text-emerald-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-lg font-bold text-emerald-700 truncate leading-tight">
                        {formatCurrency(businessPulse.forecast_revenue.amount, 0)}
                      </p>
                      <p className="text-xs text-emerald-600">Forecast Revenue</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-emerald-500 mt-2">
                    {businessPulse.forecast_revenue.job_count} job{businessPulse.forecast_revenue.job_count !== 1 ? 's' : ''} this month
                  </p>
                </CardContent>
              </Card>

              {/* 2 — Completed Jobs */}
              <Card
                className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                onClick={() => handleKPIDrilldown('completed', 'Completed This Month', `Jobs completed in ${format(today, 'MMMM yyyy')}`)}
                data-testid="bp-completed-jobs"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-7 w-7 text-green-500 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold text-green-700 leading-tight">
                        {businessPulse.completed_jobs.count}
                      </p>
                      <p className="text-xs text-green-600">Completed Jobs</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-green-400 mt-2 flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> Click to view details
                  </p>
                </CardContent>
              </Card>

              {/* 3 — Operational Backlog */}
              <Card
                className={`cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all ${businessPulse.operational_backlog.overdue_count > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}
                onClick={() => handleKPIDrilldown('overdue', 'Operational Backlog', 'Scheduled jobs that are past their completion date')}
                data-testid="bp-operational-backlog"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`h-7 w-7 shrink-0 ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-500' : 'text-slate-400'}`} />
                    <div>
                      <p className={`text-2xl font-bold leading-tight ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-700' : 'text-slate-500'}`}>
                        {businessPulse.operational_backlog.overdue_count}
                      </p>
                      <p className={`text-xs ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        Operational Backlog
                      </p>
                    </div>
                  </div>
                  <p className={`text-[10px] mt-2 flex items-center gap-1 ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                    <ExternalLink className="h-3 w-3" /> Click to view details
                  </p>
                </CardContent>
              </Card>

              {/* 4 — Sites Overdue (Management: shows Billing at Risk) */}
              <Card
                className={`cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}
                onClick={() => handleFlagDrilldown({
                  drilldown_type: 'overdue_sites',
                  title: 'Sites Overdue',
                  body: 'Sites that are past their scheduled audit frequency target date.',
                })}
                data-testid="bp-sites-overdue"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <Building2 className={`h-7 w-7 shrink-0 ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
                    <div className="min-w-0">
                      <p className={`text-2xl font-bold leading-tight ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-700' : 'text-slate-500'}`}>
                        {businessPulse.sites_compliance.total_overdue_sites}
                      </p>
                      <p className={`text-xs ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-600' : 'text-slate-500'}`}>
                        Sites Overdue
                      </p>
                    </div>
                  </div>
                  <p className={`text-[10px] mt-2 truncate ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-400' : 'text-slate-400'}`}>
                    {businessPulse.sites_compliance.billing_at_risk > 0
                      ? `${formatCurrency(businessPulse.sites_compliance.billing_at_risk, 0)} at risk`
                      : 'No billing at risk'}
                  </p>
                </CardContent>
              </Card>

              {/* 5 — First-Time Approval Rate */}
              <Card
                className="bg-blue-50 border-blue-200"
                data-testid="bp-first-time-approval"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <Trophy className="h-7 w-7 text-blue-500 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold text-blue-700 leading-tight">
                        {businessPulse.first_time_approval_rate.rate}%
                      </p>
                      <p className="text-xs text-blue-600">1st-Time Approval</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-blue-400 mt-2">
                    {businessPulse.first_time_approval_rate.approved_count}/{businessPulse.first_time_approval_rate.total_submitted} in rolling {businessPulse.first_time_approval_rate.window_days}d
                  </p>
                </CardContent>
              </Card>

            </div>
          )}

          {/* Operations Pulse — Scheduler / Senior Scheduler (non-Admin) */}
          {!loadingPulse && businessPulse && businessPulse.presentation_level === 'operations' && (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="operations-pulse-grid">

              {/* 1 — Completed Jobs */}
              <Card
                className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                onClick={() => handleKPIDrilldown('completed', 'Completed This Month', `Jobs completed in ${format(today, 'MMMM yyyy')}`)}
                data-testid="bp-completed-jobs"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-7 w-7 text-green-500 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold text-green-700 leading-tight">
                        {businessPulse.completed_jobs.count}
                      </p>
                      <p className="text-xs text-green-600">Completed Jobs</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-green-400 mt-2 flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> Click to view details
                  </p>
                </CardContent>
              </Card>

              {/* 2 — Operational Backlog */}
              <Card
                className={`cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all ${businessPulse.operational_backlog.overdue_count > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}
                onClick={() => handleKPIDrilldown('overdue', 'Operational Backlog', 'Scheduled jobs that are past their completion date')}
                data-testid="bp-operational-backlog"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`h-7 w-7 shrink-0 ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-500' : 'text-slate-400'}`} />
                    <div>
                      <p className={`text-2xl font-bold leading-tight ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-700' : 'text-slate-500'}`}>
                        {businessPulse.operational_backlog.overdue_count}
                      </p>
                      <p className={`text-xs ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        Operational Backlog
                      </p>
                    </div>
                  </div>
                  <p className={`text-[10px] mt-2 flex items-center gap-1 ${businessPulse.operational_backlog.overdue_count > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                    <ExternalLink className="h-3 w-3" /> Click to view details
                  </p>
                </CardContent>
              </Card>

              {/* 3 — Sites Overdue (Operations: secondary shows overdue_no_future, not billing) */}
              <Card
                className={`cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}
                onClick={() => handleFlagDrilldown({
                  drilldown_type: 'overdue_sites',
                  title: 'Sites Overdue',
                  body: 'Sites that are past their scheduled audit frequency target date.',
                })}
                data-testid="bp-sites-overdue"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <Building2 className={`h-7 w-7 shrink-0 ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
                    <div className="min-w-0">
                      <p className={`text-2xl font-bold leading-tight ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-700' : 'text-slate-500'}`}>
                        {businessPulse.sites_compliance.total_overdue_sites}
                      </p>
                      <p className={`text-xs ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-600' : 'text-slate-500'}`}>
                        Sites Overdue
                      </p>
                    </div>
                  </div>
                  <p className={`text-[10px] mt-2 truncate ${businessPulse.sites_compliance.total_overdue_sites > 0 ? 'text-orange-400' : 'text-slate-400'}`}>
                    {businessPulse.sites_compliance.overdue_no_future > 0
                      ? `${businessPulse.sites_compliance.overdue_no_future} with no future audit`
                      : 'All have future audits'}
                  </p>
                </CardContent>
              </Card>

              {/* 4 — First-Time Approval Rate */}
              <Card
                className="bg-blue-50 border-blue-200"
                data-testid="bp-first-time-approval"
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <Trophy className="h-7 w-7 text-blue-500 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold text-blue-700 leading-tight">
                        {businessPulse.first_time_approval_rate.rate}%
                      </p>
                      <p className="text-xs text-blue-600">1st-Time Approval</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-blue-400 mt-2">
                    {businessPulse.first_time_approval_rate.approved_count}/{businessPulse.first_time_approval_rate.total_submitted} in rolling {businessPulse.first_time_approval_rate.window_days}d
                  </p>
                </CardContent>
              </Card>

            </div>
          )}

          {/* View Performance Centre link */}
          {!loadingPulse && businessPulse && (
            <div className="flex justify-end mt-2">
              <button
                onClick={() => navigate('/performance')}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                data-testid="bp-view-performance-centre"
              >
                View Performance Centre <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Attention Required — Intelligence v1 (shown only when flags exist) */}
      {isOnline && !loadingPulse && businessPulse?.intelligence_flags?.length > 0 &&
        (isAdmin || canManageSites) && !isAuditorOnly && (
        <div
          className="rounded-lg border border-amber-200 overflow-hidden"
          data-testid="attention-required-section"
        >
          {/* Panel header */}
          <div className="px-4 py-2.5 flex items-center gap-2 bg-amber-50 border-b border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            <h2 className="text-sm font-semibold text-amber-800">Attention Required</h2>
            <span className="ml-auto text-xs text-amber-500">
              {businessPulse.intelligence_flags.length} item{businessPulse.intelligence_flags.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Flag rows */}
          <div className="divide-y divide-amber-100 bg-white">
            {businessPulse.intelligence_flags.map((flag) => (
              <button
                key={flag.flag_type}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-amber-50/60 transition-colors ${
                  flag.severity === 'critical'
                    ? 'border-l-4 border-red-500'
                    : 'border-l-4 border-amber-400'
                }`}
                onClick={() => handleFlagDrilldown(flag)}
                data-testid={`intelligence-flag-${flag.flag_type}`}
              >
                <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${
                  flag.severity === 'critical' ? 'text-red-500' : 'text-amber-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    flag.severity === 'critical' ? 'text-red-700' : 'text-amber-800'
                  }`}>
                    {flag.title}
                  </p>
                  <p className={`text-xs mt-0.5 ${
                    flag.severity === 'critical' ? 'text-red-600' : 'text-amber-700'
                  }`}>
                    {flag.body}
                  </p>
                </div>
                <ExternalLink className={`h-3.5 w-3.5 mt-1 shrink-0 ${
                  flag.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
                }`} />
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Auditor-Only KPIs - Personal stats for auditor-only users */}
      {isOnline && !loadingStats && auditorStats && isAuditorOnly && (
        <div>
          <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Your Performance This Month
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card 
              className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
              onClick={() => handleKPIDrilldown('scheduled', 'Your Scheduled Jobs', `Your jobs scheduled in ${format(today, 'MMMM yyyy')}`)}
              data-testid="auditor-kpi-scheduled"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{auditorStats.scheduled_this_month}</p>
                    <p className="text-xs text-blue-600">Your Scheduled Jobs</p>
                  </div>
                </div>
                <p className="text-[10px] text-blue-400 mt-2 flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" /> Click for breakdown
                </p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
              onClick={() => handleKPIDrilldown('completed', 'Your Completed Jobs', `Your jobs completed in ${format(today, 'MMMM yyyy')}`)}
              data-testid="auditor-kpi-completed"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-green-700">{auditorStats.completed_this_month}</p>
                    <p className="text-xs text-green-600">Completed This Month</p>
                  </div>
                </div>
                <p className="text-[10px] text-green-400 mt-2 flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" /> Click for breakdown
                </p>
              </CardContent>
            </Card>
            
            <Card 
              className={`cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all ${auditorStats.overdue_count > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}
              onClick={() => handleKPIDrilldown('overdue', 'Your Overdue Jobs', 'Your jobs past their scheduled date that are not completed')}
              data-testid="auditor-kpi-overdue"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`h-8 w-8 ${auditorStats.overdue_count > 0 ? 'text-red-500' : 'text-slate-400'}`} />
                  <div>
                    <p className={`text-2xl font-bold ${auditorStats.overdue_count > 0 ? 'text-red-700' : 'text-slate-500'}`}>
                      {auditorStats.overdue_count}
                    </p>
                    <p className={`text-xs ${auditorStats.overdue_count > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                      Overdue
                    </p>
                  </div>
                </div>
                <p className={`text-[10px] mt-2 flex items-center gap-1 ${auditorStats.overdue_count > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                  <ExternalLink className="h-3 w-3" /> Click for breakdown
                </p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-emerald-50 border-emerald-200"
              data-testid="auditor-kpi-rate"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <BarChart3 className="h-8 w-8 text-emerald-500" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">{auditorStats.completion_rate}%</p>
                    <p className="text-xs text-emerald-600">Completion Rate</p>
                  </div>
                </div>
                <p className="text-[10px] text-emerald-400 mt-2">
                  {auditorStats.completed_this_month} of {auditorStats.total_jobs} jobs
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Navigation Tiles */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <HomeIcon className="h-5 w-5" />
          Quick Navigation
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {visibleTiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <Link 
                key={tile.path} 
                to={tile.path}
                data-testid={`nav-tile-${tile.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer border-2 hover:border-slate-300">
                  <CardHeader className="pb-2">
                    <div className={`${tile.color} w-10 h-10 rounded-lg flex items-center justify-center mb-2`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <CardTitle className="text-base">{tile.title}</CardTitle>
                    <CardDescription className="text-xs">{tile.description}</CardDescription>
                    {tile.stat && (
                      <p className="text-xs font-medium text-slate-600 mt-1">{tile.stat}</p>
                    )}
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Role-specific guidance — hidden when offline */}
      {isOnline && user?.role === 'auditor' && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Your Schedule</p>
                <p className="text-sm text-blue-600">
                  Click on Schedule above to view your assigned work. You can see dates, sites, services, and notes for each assignment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isOnline && user?.role === 'scheduler' && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-800">Schedule Management</p>
                <p className="text-sm text-emerald-600">
                  Add, edit, and manage schedule entries. Use Reports to track KPIs and identify overdue work.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isOnline && isAdmin && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Briefcase className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Admin Access</p>
                <p className="text-sm text-amber-600">
                  You have full access to Customers, Services, Settings, and Reports. Use Settings to configure organisation preferences.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Drill-down Dialog */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent
          className="dialog-resizable flex flex-col"
          style={{ width: '56rem', maxHeight: '80vh' }}
          data-testid="kpi-drilldown-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {drilldownTitle}
              {!drilldownLoading && <span className="text-sm font-normal text-muted-foreground">({drilldownJobs.length} jobs)</span>}
            </DialogTitle>
            <DialogDescription>{drilldownDescription}</DialogDescription>
          </DialogHeader>
          
          {drilldownLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : drilldownJobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No jobs found for this criteria
            </div>
          ) : (
            <div
              className="overflow-y-auto flex-1 min-h-0"
              data-testid="kpi-drilldown-scroll"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Auditor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drilldownJobs.map((job) => (
                    <TableRow 
                      key={job.id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => {
                        setDrilldownOpen(false);
                        navigate('/schedule');
                      }}
                    >
                      <TableCell className="font-medium">
                        {format(new Date(job.scheduled_date), 'd MMM yyyy')}
                        {job.end_date && job.end_date !== job.scheduled_date && (
                          <span className="text-muted-foreground text-xs block">
                            → {format(new Date(job.end_date), 'd MMM')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{job.site_name || job.site?.name || '—'}</div>
                      </TableCell>
                      <TableCell>
                        {job.auditor_name ? (
                          <div className="text-xs">{job.auditor_name}</div>
                        ) : job.auditors?.length > 0 ? (
                          <div className="text-xs">
                            {job.auditors.map(a => `${a.first_name} ${a.last_name}`).join(', ')}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <div className="flex justify-start items-center pt-1.5 mt-1 border-t text-xs text-muted-foreground">
            {drilldownJobs.length} job(s) found
          </div>
        </DialogContent>
      </Dialog>

      {/* Auditor Users Drill-down Dialog */}
      <Dialog open={auditorDrilldownOpen} onOpenChange={setAuditorDrilldownOpen}>
        <DialogContent
          className="dialog-resizable"
          style={{ width: '42rem', maxHeight: '80vh' }}
          data-testid="auditor-drilldown-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Users with Auditor Role
            </DialogTitle>
            <DialogDescription>Active users who have auditor permissions</DialogDescription>
          </DialogHeader>
          
          {auditorDrilldownLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : auditorUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No users with auditor role found
            </div>
          ) : (
            <div
              className="overflow-y-auto flex-1 min-h-0"
              data-testid="auditor-drilldown-scroll"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Last Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditorUsers.map((u) => (
                    <TableRow 
                      key={u.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => {
                        setAuditorDrilldownOpen(false);
                        navigate('/users');
                      }}
                    >
                      <TableCell className="font-medium">
                        {u.first_name} {u.last_name}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {u.email}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {u.roles?.map(role => (
                            <Badge 
                              key={role} 
                              className={
                                role === 'admin' ? 'bg-red-100 text-red-700' :
                                role === 'senior_scheduler' ? 'bg-indigo-100 text-indigo-700' :
                                role === 'scheduler' ? 'bg-blue-100 text-blue-700' :
                                'bg-purple-100 text-purple-700'
                              }
                            >
                              {role === 'senior_scheduler' ? 'Senior Scheduler' : role}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground" title="Last time the user entered their email and password">
                        {u.last_login_at ? format(new Date(u.last_login_at), 'd MMM yyyy HH:mm') : 'Never'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground" title="Last successful authentication, including silent session refresh">
                        {u.last_seen_at ? format(new Date(u.last_seen_at), 'd MMM yyyy HH:mm') : 'Never'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <div className="flex justify-start items-center pt-1.5 mt-1 border-t text-xs text-muted-foreground">
            {auditorUsers.length} user(s) with auditor role
          </div>
        </DialogContent>
      </Dialog>

      {/* Intelligence Flag PC Drill-Down Dialog */}
      <PCDrilldownDialog
        open={flagDdOpen}
        onOpenChange={setFlagDdOpen}
        title={flagDdTitle}
        description={flagDdDesc}
        type={flagDdType}
        records={flagDdRecords}
        loading={flagDdLoading}
        totalRevenue={flagDdRevenue}
        totalBilling={flagDdBilling}
      />
    </div>
  );
}
